import { supabase } from '../../config/supabase';
import { Database } from '@travel-dashboard/shared';

export type Expense = Database['travel']['Tables']['expenses']['Row'];

export interface CreateExpenseInput {
  trip_id: string;
  category: string;
  description: string;
  amount: number;
  base_amount?: number | null;
  tax?: number | null;
  tip?: number | null;
  fees?: number | null;
  currency?: string;
  expense_date: string;
  is_planned?: boolean;
  merchant?: string;
  notes?: string;
  receipt_url?: string;
  original_file_name?: string;
  converted_amount: number;
  converted_currency: string;
  exchange_rate?: number;
  rate_date?: string;
  city?: string;
  country?: string;
}

export interface UpdateExpenseInput {
  category?: string;
  description?: string;
  amount?: number;
  base_amount?: number | null;
  tax?: number | null;
  tip?: number | null;
  fees?: number | null;
  currency?: string;
  expense_date?: string;
  is_planned?: boolean;
  merchant?: string;
  notes?: string;
  receipt_url?: string;
  original_file_name?: string;
  converted_amount?: number;
  converted_currency?: string;
  exchange_rate?: number;
  rate_date?: string;
  payment_method?: string;
  city?: string;
  country?: string;
}

export const expensesService = {
  /**
   * Get all expenses for a trip
   */
  async getTripExpenses(tripId: string): Promise<Expense[]> {
    const { data, error } = await supabase
      .from('expenses')
      .select('*')
      .eq('trip_id', tripId)
      .order('expense_date', { ascending: false });

    if (error) {
      console.error('Error fetching expenses:', error);
      throw error;
    }

    return data || [];
  },

  /**
   * Get expenses by category for a trip
   */
  async getExpensesByCategory(tripId: string, category: string): Promise<Expense[]> {
    const { data, error } = await supabase
      .from('expenses')
      .select('*')
      .eq('trip_id', tripId)
      .eq('category', category)
      .order('expense_date', { ascending: false });

    if (error) {
      console.error('Error fetching expenses by category:', error);
      throw error;
    }

    return data || [];
  },

  /**
   * Get planned vs actual expenses for a trip
   */
  async getPlannedExpenses(tripId: string, isPlanned: boolean): Promise<Expense[]> {
    const { data, error } = await supabase
      .from('expenses')
      .select('*')
      .eq('trip_id', tripId)
      .eq('is_planned', isPlanned)
      .order('expense_date', { ascending: false });

    if (error) {
      console.error('Error fetching planned expenses:', error);
      throw error;
    }

    return data || [];
  },

  /**
   * Get category expenses summary for a trip
   */
  async getCategoryExpenses(tripId: string) {
    const { data, error } = await supabase
      .from('category_expenses')
      .select('*')
      .eq('trip_id', tripId);

    if (error) {
      console.error('Error fetching category expenses:', error);
      throw error;
    }

    return data || [];
  },

  /**
   * Create a new expense
   */
  async createExpense(userId: string, input: CreateExpenseInput): Promise<Expense> {
    const { data, error } = await supabase
      .from('expenses')
      .insert({
        ...input,
        created_by: userId,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating expense:', error);
      throw error;
    }

    return data;
  },

  /**
   * Update an existing expense
   */
  async updateExpense(expenseId: string, input: UpdateExpenseInput): Promise<Expense> {
    const { data, error } = await supabase
      .from('expenses')
      .update(input)
      .eq('id', expenseId)
      .select()
      .single();

    if (error) {
      console.error('Error updating expense:', error);
      throw error;
    }

    return data;
  },

  /**
   * Delete an expense
   */
  async deleteExpense(expenseId: string): Promise<void> {
    const { error } = await supabase
      .from('expenses')
      .delete()
      .eq('id', expenseId);

    if (error) {
      console.error('Error deleting expense:', error);
      throw error;
    }
  },

  /**
   * Get all original file names for a trip's expenses
   */
  async getTripExpenseFileNames(tripId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('expenses')
      .select('*')
      .eq('trip_id', tripId);

    if (error) {
      console.error('Error fetching expense file names:', error);
      throw error;
    }

    return (data || [])
      .map(row => (row as Record<string, unknown>).original_file_name as string | null)
      .filter((name): name is string => name != null);
  },

  /**
   * Get a single expense by ID
   */
  async getExpenseById(expenseId: string): Promise<Expense | null> {
    const { data, error } = await supabase
      .from('expenses')
      .select('*')
      .eq('id', expenseId)
      .single();

    if (error) {
      console.error('Error fetching expense:', error);
      throw error;
    }

    return data;
  },
};
